<template>
  <div class="users-page mt-3">
    <h4>Gebruikersbeheer</h4>
    <p class="text-muted">Beheer welke gebruikers toegang hebben tot specifieke functies.</p>

    <!-- Rol toevoegen -->
    <div class="card mb-4">
      <div class="card-header">
        <i class="bi bi-person-plus me-2"></i>Gebruiker toevoegen
      </div>
      <div class="card-body">
        <form @submit.prevent="addUserRole" class="row g-3 align-items-end">
          <div class="col-md-5">
            <label for="email" class="form-label">E-mailadres</label>
            <input 
              type="email" 
              class="form-control" 
              id="email" 
              v-model="newEmail" 
              placeholder="gebruiker@klinger.nl"
              required
            />
          </div>
          <div class="col-md-4">
            <label for="role" class="form-label">Rol</label>
            <select class="form-select" id="role" v-model="newRole">
              <option value="pdf_parser">PDF Parser (AI)</option>
            </select>
          </div>
          <div class="col-md-3">
            <button type="submit" class="btn btn-primary w-100" :disabled="!newEmail || saving">
              <span v-if="saving" class="spinner-border spinner-border-sm me-1" role="status"></span>
              <i v-else class="bi bi-plus-lg me-1"></i>
              Toevoegen
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Huidige rollen -->
    <div class="card">
      <div class="card-header d-flex justify-content-between align-items-center">
        <span><i class="bi bi-people me-2"></i>Gebruikers met rollen</span>
        <span class="badge bg-secondary">{{ userRoles.length }}</span>
      </div>
      <div class="card-body p-0">
        <div v-if="loading" class="text-center py-4">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Laden...</span>
          </div>
        </div>
        <div v-else-if="userRoles.length === 0" class="text-center text-muted py-4">
          <i class="bi bi-inbox fs-1 d-block mb-2"></i>
          Nog geen gebruikers met rollen toegewezen.
        </div>
        <table v-else class="table table-hover mb-0">
          <thead>
            <tr>
              <th>E-mailadres</th>
              <th>Rol</th>
              <th>Toegevoegd op</th>
              <th class="text-end">Actie</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="userRole in userRoles" :key="userRole.id">
              <td>{{ userRole.email }}</td>
              <td>
                <span class="badge bg-info">{{ roleLabel(userRole.role) }}</span>
              </td>
              <td>{{ formatDate(userRole.created_at) }}</td>
              <td class="text-end">
                <button 
                  class="btn btn-sm btn-outline-danger" 
                  @click="removeUserRole(userRole)"
                  :disabled="deletingId === userRole.id"
                >
                  <span v-if="deletingId === userRole.id" class="spinner-border spinner-border-sm" role="status"></span>
                  <i v-else class="bi bi-trash"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Uitleg -->
    <div class="card mt-4">
      <div class="card-header">
        <i class="bi bi-info-circle me-2"></i>Rollen uitleg
      </div>
      <div class="card-body">
        <dl class="mb-0">
          <dt><span class="badge bg-info">PDF Parser (AI)</span></dt>
          <dd class="mb-0">
            Gebruikers met deze rol kunnen PDF's uploaden en laten scannen door de AI-parser om orders aan te maken.
            Ze hebben ook toegang tot het verwijderen en herstellen van orderregels bij mislukte orders.
            Ze hebben <strong>geen</strong> toegang tot de Klanten-tab of andere admin functies.
          </dd>
        </dl>
      </div>
    </div>
  </div>
</template>

<script>
import ServiceFactory from '../services/ServiceFactory';

export default {
  name: 'UsersPage',
  props: {
    isAdmin: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      userRolesService: null,
      userRoles: [],
      newEmail: '',
      newRole: 'pdf_parser',
      loading: false,
      saving: false,
      deletingId: null,
    };
  },
  created() {
    this.userRolesService = ServiceFactory.getService('UserRolesService');
  },
  async mounted() {
    await this.loadUserRoles();
  },
  methods: {
    async loadUserRoles() {
      this.loading = true;
      try {
        const result = await this.userRolesService.getAll();
        if (result.success) {
          this.userRoles = result.data;
        }
      } catch (error) {
        console.error('Fout bij laden van gebruikersrollen:', error);
        this.$emit('showToast', ['Fout bij laden van gebruikersrollen', 'bg-danger']);
      } finally {
        this.loading = false;
      }
    },

    async addUserRole() {
      if (!this.newEmail) return;

      this.saving = true;
      try {
        const result = await this.userRolesService.addRole(this.newEmail, this.newRole);
        if (result.success) {
          this.newEmail = '';
          await this.loadUserRoles();
          this.$emit('showToast', ['Gebruiker toegevoegd', 'bg-success']);
        }
      } catch (error) {
        const message = error.response?.data?.message || 'Fout bij toevoegen van gebruiker';
        this.$emit('showToast', [message, 'bg-danger']);
      } finally {
        this.saving = false;
      }
    },

    async removeUserRole(userRole) {
      if (!confirm(`Weet je zeker dat je de rol "${this.roleLabel(userRole.role)}" wilt verwijderen voor ${userRole.email}?`)) {
        return;
      }

      this.deletingId = userRole.id;
      try {
        await this.userRolesService.removeRole(userRole.id);
        await this.loadUserRoles();
        this.$emit('showToast', ['Rol verwijderd', 'bg-success']);
      } catch (error) {
        this.$emit('showToast', ['Fout bij verwijderen van rol', 'bg-danger']);
      } finally {
        this.deletingId = null;
      }
    },

    roleLabel(role) {
      const labels = {
        pdf_parser: 'PDF Parser (AI)',
      };
      return labels[role] || role;
    },

    formatDate(dateStr) {
      if (!dateStr) return '-';
      return new Date(dateStr).toLocaleDateString('nl-NL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  }
};
</script>

<style scoped>
.users-page {
  max-width: 900px;
}
</style>
